import { useEffect, useRef, useState } from 'react';
import { WbCard, WbGrid, WbGridCol, WbGridRow, WbHeading, WbSelect, WbText } from '@workbench/react';
import { useTranslation } from 'react-i18next';

import _notification from '../../../Utils/Vimdt/notification';
import _handleErrors from '../../../Utils/Vimdt/handleErrors';
import { _getEquipment, _getUrlImage, _getUrlImageAll } from '../../../Utils/Vimdt/apiCalls';
import { filterEquipmentList } from '../../../Utils/Vimdt/equipmentsHelper';
import { addToCachedData, getCachedData, hashString, STORAGE_KEYS } from '../../../Utils/Vimdt/storage';
import { marketList } from '../../../Constants/Vimdt/marketList';
import { ImageTypesRes } from '../../../Constants/Vimdt/imageTypes';
import ExampleEquipmentImages from '../../../Assets/Vimdt/ExampleEquipmentImages.json';
import { useImageContext } from '../../../Contexts/ImageContext';

import VimdtButton from '../Common/VimdtButton';
import VimdtInput from '../Common/VimdtInput';
import VimdtProductKey from '../Common/VimdtProductKey';
import EquipmentList from './EquipmentList';
import './Equipment.scss';

const CACHE_TTL_MS = 7200000;
const EQUIPMENT_CATEGORIES = ['ENGINE', 'TRIM', 'RIM', 'UPHOLSTERY', 'PAINT', 'PAINT2'];
const SAVE_FILE_NAME = 'Equipments';
const TAB_KEY = 'equipments';

const convertEquipmentImage = (imageData, imageURL) => {
	const parts = imageURL.split('.');
	const imageType = parts[parts.length - 1];
	const blob = new Blob([new Uint8Array(imageData)], { type: imageType });
	const urlCreator = window.URL || window.webkitURL;
	return { url: urlCreator.createObjectURL(blob), blob, imageType };
};

const revokeImageUrls = (list) => {
	(list || []).forEach(({ url }) => url && (window.URL || window.webkitURL).revokeObjectURL(url));
};

const revokeEquipmentUrls = (list) => {
	(list || []).forEach(({ images }) => revokeImageUrls(images));
};

const mapImageList = (source) => {
	let result = [];

	for (const equipment of EQUIPMENT_CATEGORIES) {
		const description = {};
		if (source[equipment]?.desc?.text) {
			description.text = source[equipment].desc.text;
			description.code = source[equipment].desc.code;
		}

		const tempList = Object.keys(source[equipment] || {})
			.sort()
			.reverse()
			.filter((key) => key !== 'desc')
			.map((key) => ({
				title: `${equipment} / ${key} / ${ImageTypesRes[key]}`,
				fileName: `${equipment}/${equipment}_${key}`,
				category: equipment,
				subCategory: null,
				description,
				resolution: key,
				url: source[equipment][key],
			}));

		result = result.concat(tempList.filter((obj) => obj.url != null));
	}

	return result;
};

const mapEquipmentImageList = (source) => {
	try {
		let result = [];

		Object.keys(source.EQUIPMENTS).forEach((equipment) => {
			// Reorders urls from 0,1,2,0,1,2 into 0,0,1,1,2,2 so resolutions stay grouped.
			let k = 1;
			const grouped = [];

			Object.keys(source.EQUIPMENTS[equipment])
				.sort()
				.forEach((imageTypes) => {
					if (imageTypes === 'desc') return;

					let n = 0;

					Object.keys(source.EQUIPMENTS[equipment][imageTypes].urls).forEach((urls) => {
						const description = {};
						if (source.EQUIPMENTS[equipment]?.desc?.text !== 'N/A') {
							description.code = source.EQUIPMENTS[equipment].desc.code;
							description.text = source.EQUIPMENTS[equipment].desc.text;
						}

						grouped.splice(n * k, 0, {
							title: `EQUIPMENT / ${equipment} / ${urls} / ${imageTypes} / ${ImageTypesRes[imageTypes]}`,
							fileName: `EQUIPMENT/${equipment}/${urls}/EQUIPMENT_${equipment}_${urls}_${imageTypes}`,
							category: 'EQUIPMENT',
							description,
							subCategory: equipment,
							url: source.EQUIPMENTS[equipment][imageTypes].urls[urls],
							resolution: imageTypes,
						});
						n++;
					});

					k++;
				});

			result = result.concat(grouped);
		});

		return result.concat({
			title: '',
			fileName: '',
			category: 'EQUIPMENT',
			subCategory: '',
			url: '',
			resolution: '',
		});
	} catch (error) {
		console.error('Equipment has null parameter or there is an error', error);
		return [];
	}
};

function Equipment({ rowCount = 6 }) {
	const { t } = useTranslation();
	const { tabApikeys, setTabApikey } = useImageContext();

	const [apikey, setApikey] = useState(() => tabApikeys[TAB_KEY] || null);
	const [vin, setVin] = useState(null);
	const [marketId, setMarketId] = useState('de_DE');
	const [isImagesLoading, setIsImagesLoading] = useState(false);
	const [isExampleImagesLoading, setIsExampleImagesLoading] = useState(false);
	const [imageList, setImageList] = useState([]);
	const [equipmentList, setEquipmentList] = useState([]);
	const [examples, setExamples] = useState(false);

	const prevImageList = useRef([]);
	const prevEquipmentList = useRef([]);
	const notifyKey = useRef();

	useEffect(() => {
		setTabApikey(TAB_KEY, apikey);
	}, [apikey, setTabApikey]);

	useEffect(() => {
		prevImageList.current = imageList;
	}, [imageList]);

	useEffect(() => {
		prevEquipmentList.current = equipmentList;
	}, [equipmentList]);

	useEffect(
		() => () => {
			revokeImageUrls(prevImageList.current);
			revokeEquipmentUrls(prevEquipmentList.current);
		}, []
	);

	const openNotification = (title, message) => {
		notifyKey.current = _notification({
			title,
			message,
			key: notifyKey.current,
			onClose: () => {
				notifyKey.current = undefined;
			},
		});
	};

	const getImage = (imageUrl) => _getUrlImage({ imageUrl }).then((resp) => resp.data);

	const loadImages = async (tempList) =>
		tempList.reduce(async (previousCall, nextImage) => {
			await previousCall;
			const { url, category, description, title, fileName } = nextImage;
			try {
				const imageData = await getImage(url);
				const image = convertEquipmentImage(imageData, url);
				setImageList([...prevImageList.current, { category, title, fileName, description, ...image }]);
			} catch (err) {
				_notification({
					title: t('vimdt.notification.error'),
					message: t('vimdt.notification.failedImage') + category,
					error: true,
				});
				console.error(err);
			}
		}, Promise.resolve());

	const loadEquipment = async (tempImageList) => {
		// filterEquipmentList yields a single empty bucket for vehicles without equipment.
		if (!tempImageList?.length) return;

		const splittedTitle = tempImageList[0].title.split('/');
		const name = splittedTitle[1];
		const description = tempImageList[0].description;

		let dividerName = `${splittedTitle[1]}/${splittedTitle[2]}`;
		if (description?.text && description.text !== 'N/A') {
			dividerName += `/ ${description.text}`;
		}

		if (tempImageList[0].title === '') {
			await new Promise((resolve) => setTimeout(resolve, 500));
			setEquipmentList([...prevEquipmentList.current]);
			return;
		}

		try {
			const imageDataArray = await _getUrlImageAll(tempImageList);
			const imageArray = imageDataArray.map((nextImage, index) => {
				const { category, title, fileName, description: desc, resolution } = tempImageList[index];
				return {
					category,
					title,
					fileName,
					description: desc,
					resolution,
					...convertEquipmentImage(nextImage.data, tempImageList[index].url),
				};
			});

			setEquipmentList([...prevEquipmentList.current, { images: imageArray, name, dividerName }]);
		} catch (err) {
			_notification({
				title: t('vimdt.notification.error'),
				message: t('vimdt.notification.failedImage') + dividerName,
				error: true,
			});
			console.error(err);
		}
	};

	const getEquipmentImagesFromList = async (listsPromise, vinNumber) => {
		try {
			const [newImageList, rawEquipmentList] = await listsPromise;
			const newEquipmentList = filterEquipmentList(rawEquipmentList);

			revokeImageUrls(prevImageList.current);
			revokeEquipmentUrls(prevEquipmentList.current);
			setEquipmentList([]);
			setImageList([]);

			await loadImages(newImageList);

			await newEquipmentList.reduce(async (previousCategory, equipmentCategory) => {
				await previousCategory;
				await loadEquipment(equipmentCategory.images);
			}, Promise.resolve());

			setIsExampleImagesLoading(false);
			setIsImagesLoading(false);
			openNotification(t('vimdt.notification.finished'), t('vimdt.notification.gettingImages') + vinNumber);
		} catch (err) {
			const errorMessage = _handleErrors(err, vinNumber);
			setIsImagesLoading(false);
			setIsExampleImagesLoading(false);
			openNotification(t('vimdt.notification.error'), errorMessage);
		}
	};

	const getEquipments = async () => {
		setExamples(false);
		if (!apikey || !vin) return;

		const params = { apikey, vin, marketId };

		setIsImagesLoading(true);
		if (vin.length === 17) {
			openNotification(t('vimdt.notification.loading'), t('vimdt.notification.gettingEquipments') + vin);
		}

		const cachedEquipment = getCachedData(
			STORAGE_KEYS.EQUIPMENTS,
			(item) => item.vin === vin && item.apikeyHash === hashString(apikey) && item.marketId === marketId
		);

		if (cachedEquipment) {
			getEquipmentImagesFromList(
				Promise.resolve([cachedEquipment.imageList, cachedEquipment.equipmentList]),
				vin
			);
			return;
		}

		try {
			const response = await _getEquipment(params);

			if (response.data) {
				const nextImageList = mapImageList(response.data);
				const nextEquipmentList = mapEquipmentImageList(response.data);

				addToCachedData(STORAGE_KEYS.EQUIPMENTS, {
					vin,
					apikeyHash: hashString(apikey),
					marketId,
					imageList: nextImageList,
					equipmentList: nextEquipmentList,
					expiry: new Date().getTime() + CACHE_TTL_MS,
				});

				getEquipmentImagesFromList(Promise.resolve([nextImageList, nextEquipmentList]), vin);
				return;
			}

			setIsImagesLoading(false);
			openNotification(t('vimdt.notification.error'), t('vimdt.notification.equipmentError') + vin);
		} catch (err) {
			setIsImagesLoading(false);
			openNotification(t('vimdt.notification.error'), _handleErrors(err, vin));
		}
	};

	const getExampleImages = () => {
		setIsExampleImagesLoading(true);
		setExamples(true);
		openNotification(t('vimdt.notification.loading'), t('vimdt.notification.gettingEquipmentSample'));
		getEquipmentImagesFromList(
			Promise.resolve([mapImageList(ExampleEquipmentImages), mapEquipmentImageList(ExampleEquipmentImages)]),
			'sample'
		);
	};

	return (
		<div className="vimdt-equipment">
			<WbCard className="shared-tab-content-padding">
				<WbHeading size="m">{t('vimdt.equipmentsTab.downloadText')}</WbHeading>

				<WbGrid fullWidth>
					<WbGridRow className="vimdt-equipment__inputs">
						<WbGridCol mq1={12} mq3={6}>
							<VimdtInput
								label={t('vimdt.placeholders.apiKeyPlaceholder')}
								val={apikey}
								getValue={setApikey}
								password
								testId="vimdt-equipment-apikey"
							/>
						</WbGridCol>
						<WbGridCol mq1={12} mq3={6}>
							<VimdtInput
								label={t('vimdt.placeholders.vinNumberPlaceholder')}
								getValue={setVin}
								testId="vimdt-equipment-vin"
							/>
						</WbGridCol>
					</WbGridRow>

					<WbGridRow className="vimdt-equipment__inputs">
						<WbGridCol mq1={12} mq3={6}>
							<WbSelect compact>
								<WbText slot="label" size="s">
									{t('vimdt.components.selectMarket')}
								</WbText>
								<select
									id="equipment-market-select"
									aria-label={t('vimdt.components.selectMarket')}
									value={marketId}
									onChange={(event) => setMarketId(event.target.value)}
								>
									{marketList.map((market) => (
										<option key={market.market_code} value={market.market_code}>
											{market.market_name}
										</option>
									))}
								</select>
							</WbSelect>
						</WbGridCol>
					</WbGridRow>
				</WbGrid>

				<div className="vimdt-equipment__actions">
					<VimdtButton
						label={t('vimdt.loadSaveButtons.loadSampleImages')}
						onClick={getExampleImages}
						loading={isExampleImagesLoading}
						disabled={isExampleImagesLoading || isImagesLoading}
					/>
					<VimdtButton
						label={t('vimdt.loadSaveButtons.loadImages')}
						onClick={getEquipments}
						loading={isImagesLoading}
						disabled={!apikey || !vin || isExampleImagesLoading}
					/>
				</div>
			</WbCard>

			<VimdtProductKey apikey={apikey} />

			<EquipmentList
				imageList={imageList}
				equipmentList={equipmentList}
				isImagesLoading={isImagesLoading}
				saveFileName={SAVE_FILE_NAME}
				rowCount={rowCount}
				disabled={examples}
			/>
		</div>
	);
}

export default Equipment;
